import {
  useEditSubTrayItemMutation,
  useGetSubTrayItemById,
} from 'lib/apollo/hooks'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import useTrayMap from 'views/SPDLayout/SPD/TrayMap/TrayMap.logic'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import { Typography } from '@mui/material'
import { QrCode2 } from '@mui/icons-material'
import { enqueueSnackbar } from 'notistack'
import {
  SubTrayItemProductDetails,
  TrayImageRecord,
} from 'lib/services/api/product-service/receiveItems/types'
import { useSPDScan } from 'views/SPDLayout/SPD/Scan/Scan.context'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import useGrayScaleLogic from 'components/organisms/GrayScale/GrayScale.logic'
import { getLatestTrayImage } from 'lib/utils/getLatestTrayImage'
import dayjs from 'lib/dayjs'
import { useAssignedDigitalTrays } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.context'
import useDTMS3Image from 'lib/utils/useDTMS3Image'

const useEditTray = () => {
  const navigate = useNavigate()
  const { trayId, subTrayItemId } = useParams()
  const { pathname } = useLocation()

  const {
    setTrayScrews,
    trayScrews,
    isLoadingWithUDIs,
    setIsLoadingWithUDIs,
    isAdditionalDeviceCountModalOpen,
    trayPlates,
    setTrayPlates,
  } = useTrayMap()
  const { isLoadMultipleBoxVisible } = useSPDScan()
  const {
    setParLevel,
    otherItems,
    setOtherItems,
    customImplants,
    setCustomImplants,
  } = useSPD()
  const { loading, error, subTrayItem, getSubTrayItem, refetch } =
    useGetSubTrayItemById()
  const { editSubTrayItem } = useEditSubTrayItemMutation()
  const { setSelectedSubTray } = useAssignedDigitalTrays()

  const [isInEditMode, setIsInEditMode] = useState<boolean>(false)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false)
  const [isRejecting, setIsRejecting] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [trayDetails, setTrayDetails] = useState<
    SubTrayItemProductDetails | undefined
  >(undefined)
  const [imageScale, setImageScale] = useState<number>(1)
  const [imageRotation, setImageRotation] = useState<number>(0)
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false)
  const [isCustomImplantModalOpen, setIsCustomImplantModalOpen] =
    useState<boolean>(false)

  // Image history related states
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
  const [imageHistory, setImageHistory] = useState<TrayImageRecord[]>([])
  const [isViewingHistory, setIsViewingHistory] = useState<boolean>(false)
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState<boolean>(false)

  const [
    isReferenceImageAccurateModalOpen,
    setIsReferenceImageAccurateModalOpen,
  ] = useState<boolean>(false)

  const {
    screwAnnotations,
    plateAnnotations,
    drillAnnotations,
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
  } = useGrayScaleLogic({
    trayType: trayDetails?.trayType,
  })

  const hasPlates = plateAnnotations.length > 0 || drillAnnotations.length > 0
  const hasScrews = screwAnnotations.length > 0
  const isPlatesOnly = hasPlates && !hasScrews
  const plates = trayDetails?.plates || []
  const totalPlateCount = plates.reduce(
    (acc, plate) => acc + plate.plateCount,
    0
  )
  const existingPlates = plates.filter((plate) => plate.plateCount > 0) || []
  const totalScrews = trayDetails?.screws.length

  const isInstrumentTray = trayDetails?.trayType === 'instrument'
  const isCustomImplantTray = trayDetails?.trayType === 'custom implants'

  // Image history related computed values
  const hasMultipleImages = imageHistory.length > 1
  const currentImage = imageHistory[currentImageIndex]
  const formattedTimestamp = currentImage?.timestamp
    ? dayjs(currentImage.timestamp).format('MMM D, YYYY h:mm A')
    : ''
  const rawTrayImage =
    currentImage?.imageUrl || getLatestTrayImage(trayDetails, ['rep', 'spd'])

  const { imageUrl: trayImage, isLoading: isImageLoading } = useDTMS3Image({
    url: rawTrayImage,
  })

  const getReferenceImageTitle = () => {
    if (!currentImage) return 'Reference Image'

    if (currentImage.addedBy.role === 'nurse') {
      return 'End of Case Reference Image'
    } else if (currentImage.addedBy.role === 'rep') {
      return 'Reference Image (Captured by Rep)'
    } else {
      return 'Reference Image (Captured in SPD)'
    }
  }
  const referenceImageTitle = getReferenceImageTitle()

  // Image navigation functions
  const goToPreviousImage = () => {
    if (currentImageIndex < imageHistory.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
      setIsViewingHistory(true)
    }
  }

  const goToNextImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
      setIsViewingHistory(currentImageIndex - 1 !== 0)
    }
  }

  const goToLatestImage = () => {
    setCurrentImageIndex(0)
    setIsViewingHistory(false)
  }

  useEffect(() => {
    if (isPlatesOnly) {
      setIsLoadingWithUDIs(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatesOnly])

  useEffect(() => {
    getSubTrayItem({ variables: { subTrayItemId: Number(subTrayItemId) } })
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTrayItemId, pathname])

  useEffect(() => {
    if (subTrayItem) {
      setTrayDetails(extractSubTrayJSONData(subTrayItem))
      setSelectedSubTray(subTrayItem)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTrayItem])

  useEffect(() => {
    if (subTrayItemId && !loading && !error && subTrayItem && trayDetails) {
      setTrayScrews(trayDetails.screws || [])
      setTrayPlates(trayDetails.plates || [])
      setOtherItems(trayDetails?.otherItems || [])
      setCustomImplants(trayDetails?.customImplants || [])

      setParLevel(trayDetails.parLevel || 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, loading, subTrayItemId, error, subTrayItem, trayDetails])

  // Set up image history when tray details change
  useEffect(() => {
    if (trayDetails?.trayImageHistory?.records) {
      const sortedRecords = [...trayDetails.trayImageHistory.records].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      setImageHistory(sortedRecords)
    } else if (trayDetails?.trayImg) {
      // Create a single record for legacy trayImg
      setImageHistory([
        {
          imageUrl: trayDetails.trayImg,
          addedBy: { email: 'Unknown', role: 'spd' },
          timestamp: new Date(), // Use Date object instead of string
        },
      ])
    } else {
      setImageHistory([])
    }
    // Reset to the latest image when tray details change
    setCurrentImageIndex(0)
    setIsViewingHistory(false)
  }, [trayDetails])

  const handleEditModeChange = () => {
    setIsInEditMode((prevMode) => !prevMode)
  }

  const handleViewDetailsClick = () => {
    navigate(`../${trayId}/${subTrayItemId}`)
  }

  const handleCaptureReferenceImage = () => {
    navigate(`/tray-management/${trayId}/${subTrayItemId}/capture`)
  }

  const handleConfirmationClick = (isRejecting?: boolean) => {
    if (isRejecting) {
      setIsRejecting(isRejecting)
    }

    // First check if the reference image is accurate
    setIsReferenceImageAccurateModalOpen(true)
  }

  const handleReferenceImageAccurate = () => {
    setIsReferenceImageAccurateModalOpen(false)
    setIsConfirmationOpen(true)
  }

  const handleCaptureNewReferenceImage = () => {
    setIsReferenceImageAccurateModalOpen(false)
    handleCaptureReferenceImage()
  }

  const handleLoadWithUDIsClick = () => {
    setIsLoadingWithUDIs((prev) => !prev)
  }

  const handleConfirmationClose = () => {
    setIsRejecting(false)
    setIsConfirmationOpen(false)
  }

  const handleUserConfirmation = async () => {
    if (!subTrayItem || !trayDetails) {
      return
    }
    setIsUpdating(true)

    try {
      const newProductDetails: SubTrayItemProductDetails = {
        ...trayDetails,
        screws: trayScrews,
        plates: trayPlates,
        otherItems,
        customImplants,
      }

      const editResult = await editSubTrayItem({
        id: subTrayItem.id,
        productDetails: JSON.stringify(newProductDetails),
      })

      if (editResult) {
        handleViewDetailsClick()
        setTrayDetails((prev) => (prev ? newProductDetails : undefined))
        enqueueSnackbar('Sub tray updated successfully', { variant: 'success' })
      }
    } catch (error) {
      console.error(error)
      enqueueSnackbar('Failed to update sub tray', { variant: 'error' })
    } finally {
      setIsUpdating(false)
    }

    setIsConfirmationOpen(false)
  }

  const renderSubTrayPlates = () => (
    <>
      {existingPlates.map((trayPlate) => (
        <div key={trayPlate.plateName} className="sub-tray-plate_container">
          <QrCode2 className="qr-code" />
          <div className="content">
            <Typography variant="subtitle2">
              Plate Name: {trayPlate?.plateName}
            </Typography>
            <Typography variant="subtitle2">
              Device ID: {trayPlate.deviceId}
            </Typography>
            {trayPlate.deviceDescription && (
              <Typography variant="subtitle2">
                Device Description: {trayPlate.deviceDescription}
              </Typography>
            )}
            <Typography variant="subtitle2">
              Count: {trayPlate.plateCount}
            </Typography>
          </div>
        </div>
      ))}
    </>
  )

  const handleAddCustomImplantTrigger = () => {
    setIsCustomImplantModalOpen((prev) => !prev)
  }

  return {
    isInEditMode,
    setIsInEditMode,
    handleEditModeChange,
    loading,
    error,
    subTrayItem,
    trayDetails,
    handleViewDetailsClick,
    isConfirmationOpen,
    setIsConfirmationOpen,
    handleConfirmationClick,
    handleLoadWithUDIsClick,
    isLoadingWithUDIs,
    isAdditionalDeviceCountModalOpen,
    isRejecting,
    handleConfirmationClose,
    handleUserConfirmation,
    isUpdating,
    renderSubTrayPlates,
    trayScrews,
    trayPlates,
    isLoadMultipleBoxVisible,
    isInstrumentTray,
    hasPlates,
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
    hasScrews,
    isPlatesOnly,
    totalPlateCount,
    existingPlates,
    totalScrews,
    imageScale,
    setImageScale,
    imageRotation,
    setImageRotation,
    isLegendOpen,
    setIsLegendOpen,
    // Image history related
    currentImageIndex,
    imageHistory,
    isViewingHistory,
    hasMultipleImages,
    goToPreviousImage,
    goToNextImage,
    goToLatestImage,
    trayImage,
    isImageLoading,
    formattedTimestamp,
    referenceImageTitle,
    isImagePreviewOpen,
    setIsImagePreviewOpen,
    handleCaptureReferenceImage,
    isReferenceImageAccurateModalOpen,
    setIsReferenceImageAccurateModalOpen,
    handleReferenceImageAccurate,
    handleCaptureNewReferenceImage,
    otherItems,
    setOtherItems,
    isCustomImplantTray,
    isCustomImplantModalOpen,
    handleAddCustomImplantTrigger,
  }
}

export default useEditTray
